<template>
  <VisionModal
    :open="showEmailSubscribeModal"
    :cover="true"
    hide-close-button
    :close-on-outside-click="false"
    :class="bem()"
  >
    <div :class="bem('wrapper')">
      <div :class="bem('body')">
        <div :class="bem('main')">
          <h5 :class="bem('title')">
            {{ modalTitle }}
          </h5>
          <p :class="bem('desc')">
            {{ modalDescription }}
          </p>

          <div :class="bem('options')">
            <div :class="bem('option-row')">
              <div :class="bem('option')">
                <input
                  :id="'accept-duties'"
                  v-model="acceptDuties"
                  type="checkbox"
                  :class="bem('checkbox')"
                />
                <label :for="'accept-duties'" :class="bem('checkbox-label')">
                  I understand that I am responsible for paying any additional taxes and duties on orders shipped to the United States.
                </label>
              </div>
              <VisionButton
                :disabled="!acceptDuties"
                background-colour="white"
                hover-colour="gradient"
                button-text="Continue Shopping"
                square
                :class="bem('close-btn')"
                @click="handleEmailModalClose"
              />
            </div>
          </div>

          <div :class="bem('divider')">
            <span :class="bem('divider-text')">or</span>
          </div>

          <div :class="bem('signup-section')">
            <p :class="bem('signup-title')">
              We're working hard to find a way to minimize these costs for our customers. Sign up to hear from us when we're able to ship to the US tariff-free again!
            </p>
            <SubscribeForm
              layout="row"
              :class="bem('form')"
              :show-phone-input="false"
              :rounded-form="false"
              stretch
              stack-on-mobile
              promotion-mode=""
              klaviyo-event="Submitted US Taxes and Duties Form"
              success-message="We'll keep you updated when we're able to resume shipping to the United States."
              @submitted="updateSignup"
            >
              <template #button-text>
                Keep Me Updated
              </template>
            </SubscribeForm>
          </div>
        </div>
      </div>
    </div>
  </VisionModal>
</template>

<script setup>
const bem = useBEM('TaxesAndDutiesModal')
const { $track } = useNuxtApp()

const showEmailSubscribeModal = ref(false)
const hasDismissedTaxPopup = ref(false)
const hasSuccessfulSignup = ref(false)
const acceptDuties = ref(false)

// Hardcoded content
const modalTitle = "Service Update"

const modalDescription = "Due to recent changes in import tariffs, orders shipped to the US may be subject to additional taxes and duties."

function handleEmailModalClose() {
  showEmailSubscribeModal.value = false
  localStorage.setItem('taxPopup', 'true')

  $track.taxPopup.closed({
    acceptedDuties: acceptDuties.value,
    subscribed: hasSuccessfulSignup.value
  })
}

async function updateSignup(data) {
  hasSuccessfulSignup.value = data.subscribed

  $track.taxPopup.submittedEmail({
    subscribed: data.subscribed,
    hasAlreadySubscribed: data.hasAlreadySubscribed
  })
}

onMounted(() => {
  requestIdleCallback(() => {
    hasDismissedTaxPopup.value = !!localStorage.getItem('taxPopup')
    showEmailSubscribeModal.value = !hasDismissedTaxPopup.value
    $track.taxPopup.seen()
  })
})
</script>

<style lang="postcss" scoped>
.TaxesAndDutiesModal {
  @apply lg:max-w-3xl lg:max-h-[30rem] lg:mb-10 lg:mr-10
  max-h-[calc(90vh_-_2.75rem)];

  animation: fadeUp forwards 0.3s ease-in-out;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(3rem)
  }
  to {
    opacity: 1;
    transform: translateY(0)
  }
}

.TaxesAndDutiesModal__wrapper {
  @apply flex w-full flex-col h-full lg:flex-row lg:h-[30rem] overflow-y-scroll lg:overflow-hidden rounded-md;
}

.TaxesAndDutiesModal__body {
  @apply lg:w-1/2
    flex justify-stretch items-center flex-grow
    lg:px-10 py-10 px-6
    lg:order-1 order-2
    text-left;
}

.TaxesAndDutiesModal__title {
  @apply heading-2 t-gradient pr-6;
}

.TaxesAndDutiesModal__desc,
.TaxesAndDutiesModal__signup-title {
  @apply my-3 text-off-black pr-2 lg:pr-0 text-sm;
}

.TaxesAndDutiesModal__main {
  @apply w-full;
}

.TaxesAndDutiesModal__options {
  @apply mt-6;
}

.TaxesAndDutiesModal__option-row {
  @apply flex items-center justify-between gap-4;

  @media (max-width: 640px) {
    @apply flex-col items-stretch;
  }
}

.TaxesAndDutiesModal__option {
  @apply flex items-start gap-3 flex-1;
}

.TaxesAndDutiesModal__checkbox {
  @apply mt-1;
}

.TaxesAndDutiesModal__checkbox-label {
  @apply text-sm text-off-black;
}

.TaxesAndDutiesModal__divider {
  @apply relative mt-4 mb-8 text-center;
}

.TaxesAndDutiesModal__divider::before {
  @apply content-[''] absolute top-1/2 left-0 right-0 h-px bg-gray-200;
}

.TaxesAndDutiesModal__divider-text {
  @apply relative inline-block px-4 bg-white text-sm text-gray-500;
}

.TaxesAndDutiesModal__signup-section {
  @apply mt-8;
}

.TaxesAndDutiesModal__form {
  @apply w-full;
}

.TaxesAndDutiesModal__close-btn {
  @apply whitespace-nowrap;

  @media (max-width: 640px) {
    @apply w-full mt-4;
  }
}
</style>
